import React from "react";
import { Wrench, MapPin, Phone, Mail, CheckCircle2, ShieldCheck, Navigation } from "lucide-react";

const PARTNER = {
  name: "Autoopravna Zdeněk Kalaš",
  address: "Sedlešovice 34, 671 81 Nový Šaldorf-Sedlešovice",
  phone: "+420 602 745 028",
  phoneRaw: "+420602745028",
  email: "zdenekkalas@iol.cz",
  ico: "13785559",
  mapsHref:
    "https://www.google.com/maps/dir/?api=1&destination=" +
    encodeURIComponent("Autoopravna Zdeněk Kalaš, Sedlešovice 34, 671 81 Nový Šaldorf-Sedlešovice"),
};

const SERVICES = [
  "Servis osobních a dodávkových vozidel",
  "Karosářské práce",
  "Lakýrnické práce",
  "Autoelektrika",
  "Pneuservis",
  "Příprava vozidla na STK",
  "Pomoc s technickým stavem vozidla",
];

const STEPS = [
  { n: "1", t: "Kontaktujete STK Komfort", d: "Zavoláte Lence nebo nám napíšete přes formulář či chatbota." },
  { n: "2", t: "Zjistíme, co vozidlo potřebuje", d: "Společně projdeme stav vozidla, doklady a termín, který vám vyhovuje." },
  { n: "3", t: "Doporučíme servisní přípravu", d: "Pokud je třeba, propojíme vás s doporučeným autoservisem pro kontrolu nebo opravu." },
  { n: "4", t: "Klient si s autoservisem domluví rozsah", d: "Konkrétní cenu, termín a rozsah opravy si domluvíte přímo podle stavu vozidla." },
  { n: "5", t: "Pomůžeme se zbytkem", d: "STK Komfort zorganizuje další postup okolo STK, emisí, evidenční kontroly nebo přepisu." },
];

export default function PartnerServis() {
  return (
    <section
      id="partner-servis"
      data-testid="partner-servis-section"
      className="relative bg-[#0A0D17] text-white py-20 lg:py-24 overflow-hidden"
    >
      {/* subtle gold grid background */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(212,175,55,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Heading */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-300/5 px-3 py-1 text-xs uppercase tracking-[0.22em] text-amber-300">
            <Wrench className="h-3.5 w-3.5" />
            Doporučený servisní partner
          </div>
          <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-balance">
            Partnerský servis pro přípravu vozidla
          </h2>
          <p className="font-body mt-5 text-lg text-slate-300 leading-relaxed">
            Když vozidlo potřebuje před STK opravu, kontrolu nebo technickou
            přípravu, pomůžeme vám najít jednoduché řešení. Ve spolupráci
            s doporučeným autoservisem v Sedlešovicích můžeme klientům
            doporučit servisní podporu pro osobní a dodávková vozidla.
          </p>
        </div>

        {/* Main grid: partner card + steps */}
        <div className="mt-12 grid gap-8 lg:grid-cols-5">
          {/* Partner card */}
          <div
            data-testid="partner-card"
            className="lg:col-span-3 rounded-3xl border border-amber-300/15 bg-gradient-to-br from-[#10182B] via-[#0C1224] to-[#0A0D17] p-7 sm:p-9 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.8)]"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-300/15 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold">
                  <ShieldCheck className="h-3 w-3" /> Doporučený servisní partner
                </span>
                <h3 className="font-heading mt-3 text-2xl sm:text-3xl font-bold text-white">
                  {PARTNER.name}
                </h3>
                <p className="mt-1 text-sm text-slate-400">IČO: {PARTNER.ico}</p>
              </div>
              <div className="hidden sm:flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-300/25 bg-amber-300/10">
                <Wrench className="h-6 w-6 text-amber-300" />
              </div>
            </div>

            {/* Contact rows */}
            <div className="mt-7 space-y-3 text-sm">
              <div className="flex items-start gap-3 text-slate-200">
                <MapPin className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                <span>{PARTNER.address}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-amber-300 shrink-0" />
                <a
                  href={`tel:${PARTNER.phoneRaw}`}
                  data-testid="partner-phone-link"
                  className="text-amber-200 hover:text-amber-100 font-semibold"
                >
                  {PARTNER.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-amber-300 shrink-0" />
                <a
                  href={`mailto:${PARTNER.email}`}
                  data-testid="partner-email-link"
                  className="text-amber-200 hover:text-amber-100 break-all"
                >
                  {PARTNER.email}
                </a>
              </div>
            </div>

            {/* Services */}
            <div className="mt-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300/80 font-semibold">
                Co partnerský servis zajišťuje
              </div>
              <ul className="mt-4 grid sm:grid-cols-2 gap-y-2.5 gap-x-6">
                {SERVICES.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-2.5 text-sm text-slate-200"
                  >
                    <CheckCircle2 className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Operational note */}
            <p className="mt-7 text-xs text-slate-400 leading-relaxed border-t border-white/5 pt-5">
              Příjem a výdej vozidel dle veřejně uvedené provozní doby, jinak
              podle telefonické domluvy. Konkrétní rozsah, cenu a termín opravy
              je vždy potřeba domluvit přímo podle stavu vozidla.
            </p>

            {/* CTAs */}
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={`tel:${PARTNER.phoneRaw}`}
                data-testid="partner-call-cta"
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-[0_8px_20px_-6px_rgba(212,175,55,0.5)] hover:-translate-y-0.5 transition-transform"
              >
                <Phone className="h-4 w-4" />
                Zavolat servisu
              </a>
              <a
                href={PARTNER.mapsHref}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="partner-navigate-cta"
                className="inline-flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-300/10 px-5 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-300/20 transition-all"
              >
                <Navigation className="h-4 w-4" />
                Navigovat k autoservisu
              </a>
            </div>
          </div>

          {/* Process steps */}
          <div className="lg:col-span-2">
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 sm:p-8 h-full">
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300/80 font-semibold">
                Jak to funguje
              </div>
              <h3 className="font-heading mt-2 text-xl font-bold text-white">
                Jednoduchý postup od dotazu k vyřízenému STK
              </h3>

              <ol className="mt-7 space-y-5">
                {STEPS.map((s) => (
                  <li
                    key={s.n}
                    data-testid={`partner-step-${s.n}`}
                    className="flex gap-4"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-300/10 border border-amber-300/25 text-amber-300 font-bold text-sm">
                      {s.n}
                    </span>
                    <div>
                      <div className="font-heading text-sm font-semibold text-white">
                        {s.t}
                      </div>
                      <p className="mt-1 text-sm text-slate-400 leading-relaxed">
                        {s.d}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber-300/20 bg-amber-300/[0.04] px-6 py-5">
          <div className="text-sm text-slate-300">
            <span className="font-heading text-base font-semibold text-white">
              Potřebujete připravit vozidlo na STK?
            </span>{" "}
            Domluvíme společný postup — administrativu, termín a doporučení servisu.
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="#kontakt"
              data-testid="partner-bottom-cta-order"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-900 hover:-translate-y-0.5 transition-transform"
            >
              Potřebuji připravit vozidlo na STK
            </a>
            <a
              href="#kontakt"
              data-testid="partner-bottom-cta-route"
              className="inline-flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-300/5 px-5 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-300/15 transition-all"
            >
              Domluvit další postup
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
